import React, { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../Context';
import { get, put } from '../agent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSignOutAlt,
  faEye,
  faEyeSlash,
  faUsersViewfinder,
  faUsersCog,
  faUserPlus,
  faChartBar,
  faCog,
  faFlagCheckered,
  faCogs,
  faTrash,
  faCheckCircle,
  faDollar,
  faBoxArchive,
  faCaretDown,
  faCaretRight,
  faBrush,
  faCalendar,
  faCubesStacked,
  faListCheck,
  faTriangleExclamation,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';

import logo from '../assets/images/logo-horizontal.png';
import logosimbolo from '../assets/images/logo-simbolo.png';
import appVersion from '../appVersion.json';

import { usePermissions } from '../services';
import { faNewspaper } from '@fortawesome/free-regular-svg-icons';

const Sidebar: React.FC = () => {
  const {
    canView_sketchrequests,
    canView_sketches_all,
    canView_sketches_assigned,
    canValidate_sketches,
    canView_repositions,
    canView_repositions_all,
    canView_repositions_assigned,
    canView_menu_orders,
    canView_menu_orders_created,
    canView_menu_orders_to_validate,
    canView_menu_orders_in_process,
    canView_menu_orders_finished,
    canView_menu_orders_bodega,
    canView_menu_orders_deleted,
    canView_menu_processes,
    canView_menu_repositions,
    canView_menu_repositions_created,
    canView_menu_repositions_in_process,
    canView_menu_repositions_solved,
    canView_menu_users,
    canView_menu_customers,
    canView_menu_stats,
    canView_menu_agenda,
    canView_menu_sketches,
    canView_menu_sketches_created,
    canView_menu_sketches_to_validate,
    canView_menu_sketches_changes,
    canView_menu_sketches_incomplete,
    canView_menu_sketches_approved,
    canView_menu_transactions,
    canView_menu_transactions_unassigned,
    canView_menu_transactions_assigned,
    canView_menu_supplies,
    canView_menu_audit,
    canView_menu_companies,
    canView_menu_reports,
    canView_menu_settings,
  } = usePermissions([
    'view_sketchrequests',
    'view_sketches_all',
    'view_sketches_assigned',
    'validate_sketches',
    'view_repositions',
    'view_repositions_all',
    'view_repositions_assigned',
    'view_menu_orders',
    'view_menu_orders_created',
    'view_menu_orders_to_validate',
    'view_menu_orders_in_process',
    'view_menu_orders_finished',
    'view_menu_orders_bodega',
    'view_menu_orders_deleted',
    'view_menu_processes',
    'view_menu_repositions',
    'view_menu_repositions_created',
    'view_menu_repositions_in_process',
    'view_menu_repositions_solved',
    'view_menu_users',
    'view_menu_customers',
    'view_menu_stats',
    'view_menu_agenda',
    'view_menu_sketches',
    'view_menu_sketches_created',
    'view_menu_sketches_to_validate',
    'view_menu_sketches_changes',
    'view_menu_sketches_incomplete',
    'view_menu_sketches_approved',
    'view_menu_transactions',
    'view_menu_transactions_unassigned',
    'view_menu_transactions_assigned',
    'view_menu_supplies',
    'view_menu_audit',
    'view_menu_companies',
    'view_menu_reports',
    'view_menu_settings',
  ]);
  const canViewRepositions = canView_repositions || canView_repositions_all || canView_repositions_assigned;
  const canViewOrdersGroup =
    canView_menu_orders &&
    (canView_menu_orders_created ||
      canView_menu_orders_to_validate ||
      canView_menu_orders_in_process ||
      canView_menu_orders_finished ||
      canView_menu_orders_bodega ||
      canView_menu_orders_deleted);
  const canViewSketchesGroup =
    canView_menu_sketches &&
    (canView_menu_sketches_created ||
      canView_menu_sketches_to_validate ||
      canView_menu_sketches_changes ||
      canView_menu_sketches_incomplete ||
      canView_menu_sketches_approved);
  const canViewSketchCounts = canView_sketchrequests || canViewSketchesGroup;
  const canViewRepositionsGroup =
    canView_menu_repositions &&
    (canView_menu_repositions_created || canView_menu_repositions_in_process || canView_menu_repositions_solved);
  const canViewTransactionsGroup =
    canView_menu_transactions && (canView_menu_transactions_unassigned || canView_menu_transactions_assigned);
  const canViewRepositionsForMenu = canViewRepositions || canViewRepositionsGroup;

  const { logout, setOpen, open, auth } = useAppContext();
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const [isOrdersOpen, setOrdersOpen] = useState(false); // Estado para controlar el desplegable "Órdenes"
  const [isTransactionsOpen, setTransactionsOpen] = useState(false); // Estado para controlar el desplegable "Movimientos"
  const [isSketchsOpen, setSketchsOpen] = useState(false); // Estado para controlar el desplegable "Bocetos"
  const [isRepositionsOpen, setRepositionsOpen] = useState(false); // Estado para controlar el desplegable "Reposiciones"
  const [sketchSidebarCounts, setSketchSidebarCounts] = useState({
    created: 0,
    pending_validation: 0,
    changes_requested: 0,
    incomplete_resources: 0,
  });
  const [repositionsSidebarCounts, setRepositionsSidebarCounts] = useState({
    created: 0,
    in_process: 0,
    solved: 0,
  });
  const [companies, setCompanies] = useState<Array<{ id: number; name: string; is_active?: boolean }>>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<number | null>(null);

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();
  const isSuperuser = Boolean(auth.user?.is_superuser || storedUser?.is_superuser);
  const allowTenantNav = !isSuperuser || Boolean(activeCompanyId);
  const companyLabel = auth.user?.company?.name || storedUser?.company?.name;
  const appVersionLabel = `${appVersion.frontend}/${appVersion.backend}`;

  const fetchSketchesSidebarCounts = async () => {
    if (!allowTenantNav || !canViewSketchCounts) {
      return;
    }
    try {
      const response = await get('sketches/sidebar-counts/', null);
      if (!response.ok) return;
      const data = await response.json();
      setSketchSidebarCounts({
        created: data.created ?? 0,
        pending_validation: data.pending_validation ?? 0,
        changes_requested: data.changes_requested ?? 0,
        incomplete_resources: data.incomplete_resources ?? 0,
      });
    } catch (error) {
      return;
    }
  };

  const fetchRepositionsSidebarCounts = async () => {
    if (!allowTenantNav || !canViewRepositionsForMenu) {
      return;
    }
    try {
      const response = await get('repositions/sidebar-counts/', null);
      if (!response.ok) return;
      const data = await response.json();
      setRepositionsSidebarCounts({
        created: data.created ?? 0,
        in_process: data.in_process ?? 0,
        solved: data.solved ?? 0,
      });
    } catch (error) {
      return;
    }
  };

  useEffect(() => {
    if (isSuperuser) {
      fetchCompanies();
      fetchActiveCompany();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!allowTenantNav) {
      return;
    }
    fetchSketchesSidebarCounts();
    fetchRepositionsSidebarCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowTenantNav]);

  const fetchCompanies = async () => {
    try {
      const response = await get('companies/', null);
      if (!response.ok) return;
      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      return;
    }
  };

  const fetchActiveCompany = async () => {
    try {
      const response = await get('companies/active/', null);
      if (!response.ok) return;
      const data = await response.json();
      setActiveCompanyId(data?.active_company?.id ?? null);
      updateStoredUser({ active_company: data?.active_company ?? null });
    } catch (error) {
      return;
    }
  };

  const updateStoredUser = (updates: any) => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const nextUser = { ...currentUser, ...updates };
    localStorage.setItem('user', JSON.stringify(nextUser));
  };

  const handleActiveCompanyChange = async (companyId: number) => {
    try {
      const response = await put('companies/active/', { company_id: companyId });
      if (!response.ok) return;
      const data = await response.json();
      setActiveCompanyId(data?.active_company?.id ?? null);
      updateStoredUser({ active_company: data?.active_company ?? null });
    } catch (error) {
      return;
    }
  };

  const handleToggleSketches = () => {
    setOrdersOpen(false);
    const nextState = !isSketchsOpen;
    setSketchsOpen(nextState);
    if (nextState) {
      fetchSketchesSidebarCounts();
    }
  };

  const handleToggleRepositions = () => {
    setOrdersOpen(false);
    setTransactionsOpen(false);
    setSketchsOpen(false);
    const nextState = !isRepositionsOpen;
    setRepositionsOpen(nextState);
    if (nextState) {
      fetchRepositionsSidebarCounts();
    }
  };

  const handleToggleSidebar = () => {
    setOpen(!open);
    fetchSketchesSidebarCounts();
    fetchRepositionsSidebarCounts();
  };

  return (
    <SidebarContainer className={open ? 'open' : 'close'}>
      <HiddenButton className={open ? 'open' : 'close'} onClick={handleToggleSidebar}>
        <img src={logosimbolo} className={open ? 'open' : 'close'} alt='logo' />
        <FontAwesomeIcon icon={open ? faEyeSlash : faEye} />
      </HiddenButton>
      {open ? (
        <>
          <div className='sidebar-header'>
            <img src={logo} alt='logo' />
            {isSuperuser ? (
              <CompanySwitcher>
                <>
                  <span>Empresa activa</span>
                  <select
                    value={activeCompanyId ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        handleActiveCompanyChange(0);
                      } else {
                        handleActiveCompanyChange(Number(value));
                      }
                    }}
                  >
                    <option value=''>Sin seleccionar</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id} disabled={company.is_active === false}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </>
              </CompanySwitcher>
            ) : companyLabel ? (
              <CompanySwitcher>
                <span style={{color:'white'}}>{companyLabel}</span>
              </CompanySwitcher>
            ) : null}
            <div className='sidebar-version'>{appVersionLabel}</div>
          </div>
          <div className='sidebar-body' style={{ overflowY: 'auto' }}>
            {!allowTenantNav ? (
              <>
                {canView_menu_companies && isSuperuser && (
                  <SidebarItem onClick={() => handleNavigate('empresas/')}>
                    <FontAwesomeIcon icon={faBuilding} />
                    <span>Empresas</span>
                  </SidebarItem>
                )}
              </>
            ) : (
              <>
                {/* Grupo de Órdenes */}
                {canViewOrdersGroup && (
                  <>
                    <SidebarItem
                      onClick={() => {
                        setTransactionsOpen(false);
                        setOrdersOpen(!isOrdersOpen);
                      }}
                    >
                      <div className='sidebar-item-icon'>
                        <FontAwesomeIcon icon={isOrdersOpen ? faCaretDown : faCaretRight} />
                      </div>
                      <div className='sidebar-item-text'>Órdenes</div>
                    </SidebarItem>
                    {isOrdersOpen && (
                      <DropdownContainer>
                        <>
                          {canView_menu_orders_created && (
                            <SidebarItem onClick={() => handleNavigate('ordenes/')}>
                              <FontAwesomeIcon icon={faUsersViewfinder} />
                              <span>Órdenes Creadas</span>
                            </SidebarItem>
                          )}
                          {canView_menu_orders_to_validate && (
                            <SidebarItem onClick={() => handleNavigate('ordenes/por-validar')}>
                              <FontAwesomeIcon icon={faCheckCircle} />
                              <span>Órdenes Por Validar</span>
                            </SidebarItem>
                          )}
                          {canView_menu_orders_in_process && (
                            <SidebarItem onClick={() => handleNavigate('ordenes/proceso')}>
                              <FontAwesomeIcon icon={faCogs} />
                              <span>Órdenes en Proceso</span>
                            </SidebarItem>
                          )}
                          {canView_menu_orders_finished && (
                            <SidebarItem onClick={() => handleNavigate('ordenes/finalizadas')}>
                              <FontAwesomeIcon icon={faFlagCheckered} />
                              <span>Órdenes Finalizadas</span>
                            </SidebarItem>
                          )}
                          {canView_menu_orders_bodega && (
                            <SidebarItem onClick={() => handleNavigate('ordenes/bodega')}>
                              <FontAwesomeIcon icon={faBoxArchive} />
                              <span>Bodega</span>
                            </SidebarItem>
                          )}
                        </>
                        {canView_menu_orders_deleted && (
                          <SidebarItem onClick={() => handleNavigate('ordenes/eliminadas')}>
                            <FontAwesomeIcon icon={faTrash} />
                            <span>Órdenes Eliminadas</span>
                          </SidebarItem>
                        )}
                      </DropdownContainer>
                    )}
                  </>
                )}
                {/* Grupo de Procesos */}

            {canView_menu_processes ? (
              <SidebarItem onClick={() => handleNavigate('procesos/')}>
                <div className='sidebar-item-icon'>
                  <FontAwesomeIcon icon={faUsersCog} />
                </div>
                <div className='sidebar-item-text'>Procesos</div>
              </SidebarItem>
            ) : null}
            {canViewRepositionsGroup ? (
              <>
                <SidebarItem onClick={handleToggleRepositions}>
                  <div className='sidebar-item-icon'>
                    <FontAwesomeIcon icon={isRepositionsOpen ? faCaretDown : faCaretRight} />
                  </div>
                  <div className='sidebar-item-text'>Reposiciones</div>
                </SidebarItem>
                {isRepositionsOpen && (
                  <DropdownContainer>
                    <>
                      {canView_menu_repositions_created && (
                        <SidebarItem onClick={() => handleNavigate('reposiciones/')}>
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                          <span>Creadas</span>
                          {repositionsSidebarCounts.created > 0 ? (
                            <SidebarBadge>{repositionsSidebarCounts.created}</SidebarBadge>
                          ) : null}
                        </SidebarItem>
                      )}
                      {canView_menu_repositions_in_process && (
                        <SidebarItem onClick={() => handleNavigate('reposiciones/en-proceso')}>
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                          <span>En Proceso</span>
                          {repositionsSidebarCounts.in_process > 0 ? (
                            <SidebarBadge>{repositionsSidebarCounts.in_process}</SidebarBadge>
                          ) : null}
                        </SidebarItem>
                      )}
                      {canView_menu_repositions_solved && (
                        <SidebarItem onClick={() => handleNavigate('reposiciones/resueltas')}>
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                          <span>Resueltas</span>
                          {repositionsSidebarCounts.solved > 0 ? (
                            <SidebarBadge>{repositionsSidebarCounts.solved}</SidebarBadge>
                          ) : null}
                        </SidebarItem>
                      )}
                    </>
                  </DropdownContainer>
                )}
              </>
            ) : null}
            {canView_menu_users && (
              <SidebarItem onClick={() => handleNavigate('usuarios/')}>
                <FontAwesomeIcon icon={faUserPlus} />
                <span>Usuarios</span>
              </SidebarItem>
            )}
            {canView_menu_customers ? (
              <SidebarItem onClick={() => handleNavigate('clientes/')}>
                <FontAwesomeIcon icon={faUsersViewfinder} />
                <span>Clientes</span>
              </SidebarItem>
            ) : null}
            {canView_menu_stats && (
              <SidebarItem onClick={() => handleNavigate('estadisticas/')}>
                <FontAwesomeIcon icon={faChartBar} />
                <span>Estadísticas</span>
              </SidebarItem>
            )}
            {canView_menu_agenda && (
              <SidebarItem onClick={() => handleNavigate('agenda/')}>
                <FontAwesomeIcon icon={faCalendar} />
                <span>Agenda</span>
              </SidebarItem>
            )}
            {canViewSketchesGroup && (
              <>
                <SidebarItem onClick={handleToggleSketches}>
                  <div className='sidebar-item-icon'>
                    <FontAwesomeIcon icon={isSketchsOpen ? faCaretDown : faCaretRight} />
                  </div>
                  <div className='sidebar-item-text'>Bocetos</div>
                </SidebarItem>
                {isSketchsOpen && (
                  <DropdownContainer>
                    <>
                      {canView_menu_sketches_created && (
                        <SidebarItem onClick={() => handleNavigate('bocetos/creados')}>
                          <FontAwesomeIcon icon={faBrush} />
                          <span>Creados</span>
                          {sketchSidebarCounts.created > 0 ? (
                            <SidebarBadge>{sketchSidebarCounts.created}</SidebarBadge>
                          ) : null}
                        </SidebarItem>
                      )}
                      {canView_menu_sketches_to_validate && canValidate_sketches && (
                        <SidebarItem onClick={() => handleNavigate('bocetos/por-validar')}>
                          <FontAwesomeIcon icon={faBrush} />
                          <span>Por Validar</span>
                          {sketchSidebarCounts.pending_validation > 0 ? (
                            <SidebarBadge>{sketchSidebarCounts.pending_validation}</SidebarBadge>
                          ) : null}
                        </SidebarItem>
                      )}
                      {canView_menu_sketches_changes && (
                        <SidebarItem onClick={() => handleNavigate('bocetos/con-cambios')}>
                          <FontAwesomeIcon icon={faBrush} />
                          <span>Con Cambios</span>
                          {(canView_sketches_all || canView_sketches_assigned) &&
                          sketchSidebarCounts.changes_requested > 0 ? (
                            <SidebarBadge>{sketchSidebarCounts.changes_requested}</SidebarBadge>
                          ) : null}
                        </SidebarItem>
                      )}
                      {canView_menu_sketches_incomplete && (
                        <SidebarItem onClick={() => handleNavigate('bocetos/recursos-incompletos')}>
                          <FontAwesomeIcon icon={faBrush} />
                          <span>Recursos incompletos</span>
                          {sketchSidebarCounts.incomplete_resources > 0 ? (
                            <SidebarBadge>{sketchSidebarCounts.incomplete_resources}</SidebarBadge>
                          ) : null}
                        </SidebarItem>
                      )}
                      {canView_menu_sketches_approved && (
                        <SidebarItem onClick={() => handleNavigate('bocetos/aprobados')}>
                          <FontAwesomeIcon icon={faBrush} />
                          <span>Aprobados</span>
                        </SidebarItem>
                      )}
                    </>
                  </DropdownContainer>
                )}
              </>
            )}
            {/* Grupo de Movimientos */}
            {canViewTransactionsGroup && (
              <>
                <SidebarItem
                  onClick={() => {
                    setOrdersOpen(false);
                    setTransactionsOpen(!isTransactionsOpen);
                  }}
                >
                  <div className='sidebar-item-icon'>
                    <FontAwesomeIcon icon={isTransactionsOpen ? faCaretDown : faCaretRight} />
                  </div>
                  <div className='sidebar-item-text'>Movimientos</div>
                </SidebarItem>
                {isTransactionsOpen && (
                  <DropdownContainer>
                    <>
                      {canView_menu_transactions_unassigned && (
                        <SidebarItem onClick={() => handleNavigate('/movimientos/')}>
                          <FontAwesomeIcon icon={faDollar} />
                          <span>Movimientos Sin Asignar</span>
                        </SidebarItem>
                      )}
                      {canView_menu_transactions_assigned && (
                        <SidebarItem onClick={() => handleNavigate('/movimientos-asignados/')}>
                          <div className='sidebar-item-icon'>
                            <FontAwesomeIcon icon={faDollar} />
                          </div>
                          <div className='sidebar-item-text'>Movimientos Asignados</div>
                        </SidebarItem>
                      )}
                    </>
                  </DropdownContainer>
                )}
              </>
            )}
            {canView_menu_supplies && (
              <SidebarItem onClick={() => handleNavigate('insumos/')}>
                <FontAwesomeIcon icon={faCubesStacked} />
                <span>Suministros</span>
              </SidebarItem>
            )}
            {canView_menu_audit && (
              <SidebarItem onClick={() => handleNavigate('auditoria/')}>
                <FontAwesomeIcon icon={faListCheck} />
                <span>Auditoria</span>
              </SidebarItem>
            )}
            {canView_menu_companies && isSuperuser && (
              <SidebarItem onClick={() => handleNavigate('empresas/')}>
                <FontAwesomeIcon icon={faBuilding} />
                <span>Empresas</span>
              </SidebarItem>
            )}
            {canView_menu_reports && (
              <SidebarItem onClick={() => handleNavigate('informes/')}>
                <FontAwesomeIcon icon={faNewspaper} />
                <span>Informes</span>
              </SidebarItem>
            )}
              </>
            )}
          </div>
          {/* Footer */}
          {allowTenantNav && canView_menu_settings ? (
            <div className='sidebar-footer'>
              <SidebarItem onClick={() => handleNavigate('configuraciones/')}>
                <div className='sidebar-item-icon'>
                  <FontAwesomeIcon icon={faCog} />
                </div>
                <div className='sidebar-item-text'>Configuraciones</div>
              </SidebarItem>
            </div>
          ) : null}
          <div className='sidebar-footer'>
            <SidebarItem onClick={handleLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Cerrar sesión</span>
            </SidebarItem>
          </div>
        </>
      ) : null}
    </SidebarContainer>
  );
};

const SidebarContainer = styled.div`
  width: 240px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--blueLight);
  border-right: 1px solid #f1f1f1;
  transition: all 0.3s ease;
  position: fixed;
  left: 0;
  z-index: 999;
  .sidebar-header {
    padding: 10px;
    border-bottom: 1px solid #f1f1f1;
    img {
      width: 100%;
    }
    .sidebar-version {
      margin-top: 6px;
      font-size: 11px;
      font-weight: 600;
      color: #ffffff;
      opacity: 0.9;
    }
  }
  .sidebar-body {
    padding: 10px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    flex: 1;
  }
  .sidebar-footer {
    padding: 10px;
    border-top: 1px solid #f1f1f1;
  }
  &.open {
    left: 0;
  }
  &.close {
    left: -240px;
  }
`;

const SidebarItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    color: var(--blueDark);
  }
  svg {
    margin-right: 10px;
  }
`;

const DropdownContainer = styled.div`
  padding-left: 20px;
  display: flex;
  flex-direction: column;
`;

const SidebarBadge = styled.span`
  background: #e53935;
  color: #fff;
  border-radius: 50%;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  margin-left: 8px;
  line-height: 1;
`;

const CompanySwitcher = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: var(--blueDark);
  span {
    font-weight: 600;
  }
  select {
    font-size: 12px;
    padding: 6px;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    background: #fff;
  }
`;

const HiddenButton = styled.div`
  position: absolute;
  background: #0b4a87;
  display: flex;
  left: 240px;
  border-radius: 0px 25px 25px 0px;
  height: 40px;
  justify-content: center;
  align-items: center;
  box-shadow: 1px 1px 3px #010101;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1000;
  &:hover {
    background-color: var(--blueDark);
  }
  &.open {
    right: 210px;
    width: 40px;
  }
  &.close {
    right: 10px;
    width: 80px;
  }
  img {
    width: 30px;
    margin-right: 5px;
  }
  img.open {
    display: none;
  }
  img.close {
    display: block;
  }
`;

export default memo(Sidebar);
